import { Button, Group, Paper, Select, Title } from '@mantine/core';
import { IconFileDownload } from '@tabler/icons-react';
import { RowSelectionState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ValidationFormsTable from '../../components/validation-forms-table/validation-forms-table';

import { ErrorPage } from './page.error';
import { useValidationFormsExport } from './use-validation-forms-export';

import { useCampaigns, useFiltersStore } from '@data-access';
import { FullMainHeight } from '@ui';

const CLAUSE_OPTIONS = ['new', 'gp'] as const;

export function ValidationFormsPage() {
  const { t } = useTranslation();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const filters = useFiltersStore.use['validationForms']().filters;
  const sorting = useFiltersStore.use['validationForms']().sorting;
  const { handleExport, isExportLoading } = useValidationFormsExport({
    filters,
    sorting,
  });
  const setFilterAndResetPage = useFiltersStore(
    (state) => state.setFilterAndResetPage,
  );
  const campaigns = useCampaigns();

  const clause =
    filters.find((filter) => filter.id === 'clause')?.value === 'gp'
      ? 'gp'
      : 'new';
  const campaignYear = String(
    filters.find((filter) => filter.id === 'campaign_year')?.value ?? '2026',
  );

  const yearOptions = useMemo(() => {
    const years = new Set<number>([2026]);
    campaigns.data?.forEach((campaign) => years.add(campaign.year));
    return [...years]
      .sort((left, right) => right - left)
      .map((year) => ({ value: String(year), label: String(year) }));
  }, [campaigns.data]);

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <Paper>
          <Group justify="space-between" wrap="wrap">
            <Title order={2}>Suivi agriculteurs</Title>
            <Group gap="sm" wrap="wrap">
              <Group
                aria-label={t('common.properties.clause')}
                gap="xs"
                role="radiogroup"
              >
                {CLAUSE_OPTIONS.map((option) => (
                  <Button
                    aria-checked={clause === option}
                    key={option}
                    onClick={() => {
                      setFilterAndResetPage(
                        'validationForms',
                        'clause',
                        option,
                      );
                      setRowSelection({});
                    }}
                    role="radio"
                    type="button"
                    variant={clause === option ? 'filled' : 'default'}
                  >
                    {t(`clauses.${option}`)}
                  </Button>
                ))}
              </Group>
              <Select
                allowDeselect={false}
                aria-label={t('common.campaign')}
                data={yearOptions}
                onChange={(value) => {
                  if (value) {
                    setFilterAndResetPage(
                      'validationForms',
                      'campaign_year',
                      value,
                    );
                    setRowSelection({});
                  }
                }}
                value={campaignYear}
                w={120}
              />
              <Button
                leftSection={<IconFileDownload size={16} />}
                loading={isExportLoading}
                onClick={handleExport}
                type="button"
                variant="default"
              >
                {t('common.download_recap')}
              </Button>
            </Group>
          </Group>
        </Paper>
      </FullMainHeight.Header>
      <FullMainHeight.Content>
        <ValidationFormsTable
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

ValidationFormsPage.Error = ErrorPage;

export default ValidationFormsPage;
